// useAutoSave.js - Hook for easy auto-save integration
import { useCallback, useEffect, useRef } from 'react';
import useAutoSaveStore from '../stores/autoSaveStore';
import changeDetector from '../services/ChangeDetector';

/**
 * Hook for auto-save functionality
 * @param {string} subcategoryId - ID of the subcategory
 * @param {object} options - Configuration options
 * @returns {object} - Auto-save utilities and status
 */
export const useAutoSave = (subcategoryId, options = {}) => {
  const {
    enabled = true,
    debounceDelay = 2000,
    changeThreshold = 50,
    onSaveSuccess = null,
    onSaveError = null,
    onSaveStart = null
  } = options;

  const {
    markChange,
    getSaveStatus,
    hasUnsavedChanges,
    getSaveError,
    clearSaveError,
    isAutoSaveEnabled
  } = useAutoSaveStore();

  const debounceTimer = useRef(null);
  const lastSavedData = useRef(null);

  // Initialize auto-save store
  useEffect(() => {
    useAutoSaveStore.getState().initializeAutoSave();
  }, []);

  // Configure change detector
  useEffect(() => {
    changeDetector.changeThreshold = changeThreshold;
  }, [changeThreshold]);

  // Auto-save function with debouncing
  const autoSave = useCallback((changeType, data) => {
    if (!enabled || !isAutoSaveEnabled) {
      return;
    }

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Check if data has actually changed
    if (lastSavedData.current && 
        JSON.stringify(lastSavedData.current) === JSON.stringify(data)) {
      return;
    }

    // Call onSaveStart callback
    if (onSaveStart) {
      onSaveStart(changeType, data);
    }

    // Debounce the save
    debounceTimer.current = setTimeout(() => {
      markChange(subcategoryId, changeType, data);
      lastSavedData.current = data;
    }, debounceDelay);

  }, [enabled, isAutoSaveEnabled, subcategoryId, debounceDelay, markChange, onSaveStart]);

  // Save content changes
  const saveContent = useCallback((content) => {
    autoSave('content', content);
  }, [autoSave]);

  // Save privacy changes
  const savePrivacy = useCallback((privacy) => {
    autoSave('privacy', privacy);
  }, [autoSave]);

  // Save file changes
  const saveFiles = useCallback((files) => {
    autoSave('files', files);
  }, [autoSave]);

  // Force save immediately
  const forceSave = useCallback((changeType, data) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    markChange(subcategoryId, changeType, data);
    lastSavedData.current = data;
  }, [subcategoryId, markChange]);

  // Get current save status
  const saveStatus = getSaveStatus(subcategoryId);
  const hasUnsaved = hasUnsavedChanges(subcategoryId);
  const saveError = getSaveError(subcategoryId);

  // Handle save events
  useEffect(() => {
    const handleSaveEvent = (event) => {
      const { type, subcategoryId: eventSubcategoryId } = event.detail;
      
      if (eventSubcategoryId !== subcategoryId) {
        return;
      }

      switch (type) {
        case 'success':
          if (onSaveSuccess) {
            onSaveSuccess(event.detail);
          }
          break;
        case 'error':
          if (onSaveError) {
            onSaveError(event.detail);
          }
          break;
      }
    };

    window.addEventListener('autosave', handleSaveEvent);
    return () => window.removeEventListener('autosave', handleSaveEvent);
  }, [subcategoryId, onSaveSuccess, onSaveError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return {
    // Save functions
    saveContent,
    savePrivacy,
    saveFiles,
    forceSave,
    
    // Status
    saveStatus: saveStatus.status,
    hasUnsavedChanges: hasUnsaved,
    saveError,
    isSaving: saveStatus.status === 'saving' || saveStatus.status === 'pending',
    isSaved: saveStatus.status === 'saved' && !hasUnsaved,
    hasError: saveStatus.status === 'error',
    
    // Utilities
    clearError: () => clearSaveError(subcategoryId),
    lastSaved: saveStatus.lastSaved,
    retryCount: saveStatus.retryCount,
    
    // Configuration
    isAutoSaveEnabled,
    enabled
  };
};

/**
 * Hook for Editor.js auto-save integration
 * @param {string} subcategoryId - ID of the subcategory
 * @param {object} options - Configuration options
 * @returns {object} - Editor-specific auto-save utilities
 */
export const useEditorAutoSave = (subcategoryId, options = {}) => {
  const autoSave = useAutoSave(subcategoryId, options);
  
  // Save editor content
  const saveEditorContent = useCallback((editorData) => {
    autoSave.saveContent(editorData);
  }, [autoSave]);

  // Save editor content on change
  const handleEditorChange = useCallback((api, event) => {
    const editorData = api.saver.save();
    saveEditorContent(editorData);
  }, [saveEditorContent]);

  return {
    ...autoSave,
    saveEditorContent,
    handleEditorChange
  };
};

/**
 * Hook for form auto-save integration
 * @param {string} subcategoryId - ID of the subcategory
 * @param {object} options - Configuration options
 * @returns {object} - Form-specific auto-save utilities
 */
export const useFormAutoSave = (subcategoryId, options = {}) => {
  const autoSave = useAutoSave(subcategoryId, options);
  
  // Save form data
  const saveFormData = useCallback((formData) => {
    autoSave.saveContent(formData);
  }, [autoSave]);

  // Handle form field changes
  const handleFieldChange = useCallback((fieldName, value) => {
    const formData = { [fieldName]: value };
    saveFormData(formData);
  }, [saveFormData]);

  return {
    ...autoSave,
    saveFormData,
    handleFieldChange
  };
};

export default useAutoSave;
